import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Column, Divider, Row, SmartTooltip, Text } from '~/commonComponents';
import { JobPostingStatus, JobPostingStatusMapper } from '~/domain/PermanentJobs';
import { useJobProfessionDisplay } from '~/hooks';
import { WarningSolidIcon } from '~/images/icons';
import UserIcon from '~/images/user-icon.svg';
import { Colors } from '~/themes/colors';
import { Badge } from '../../../components';
import { TJobCardProps } from './Types';

export const JobCard = ({
  candidateCount,
  closedAt,
  id,
  office,
  onsiteInterviewsCount,
  professionType,
  publishedAt,
  status,
  subProfession,
  title,
}: TJobCardProps) => {
  const navigate = useNavigate();

  const fmtTimestamp = useMemo(() => {
    if (status === JobPostingStatus.Closed) {
      return `Closed on ${closedAt}`;
    }

    return `Posted on ${publishedAt}`;
  }, [closedAt, publishedAt, status]);

  const hasNoInterviewSlots = onsiteInterviewsCount === 0;

  const isActiveJob = status === JobPostingStatus.Active;
  const isSuspendedJob = status === JobPostingStatus.Suspended;

  const isShowInterviewCounter =
    isActiveJob && onsiteInterviewsCount !== undefined && onsiteInterviewsCount <= 3;
  const totalCandidates = candidateCount ?? 0;

  const professionTypeDisplay = useJobProfessionDisplay({
    profession: professionType,
    subProfession,
  });

  return (
    <Row
      alignItems="center"
      backgroundColor={Colors.white}
      borderRadius={10}
      className="cursor-pointer shadow-xs hover__shadow-sm"
      data-cy="active-job-card"
      display="grid"
      gridTemplateColumns="1fr 20% 20%"
      key={id}
      paddingBlock={12}
      onClick={() => navigate(`/permanent-jobs/job-overview/${id}`)}
    >
      <Column gap={6} marginLeft={36}>
        <Row alignItems="center" gap={8}>
          <Text bold textDecoration="underline">
            {title || 'UNTITLED'}
          </Text>
          {isSuspendedJob && (
            <SmartTooltip
              content="Awaiting Payment"
              hideArrow
              position="bottom"
              style={{
                Content: { fontSize: 16, paddingBlock: 1, paddingInline: 10 },
                Portal: { borderRadius: 3 },
              }}
            >
              <WarningSolidIcon color={Colors.warning_500} size={18} />
            </SmartTooltip>
          )}
        </Row>
        <Text>{office?.name}</Text>
        <Row alignItems="center" gap={12}>
          <Text>{professionTypeDisplay || 'No Professional Type Selected'}</Text>
          <Divider width={1} height={12} marginVertical={0} />
          <Typography italic color={colors.neutral[400]} style={{ fontSize: 14 }}>
            {fmtTimestamp}
          </Typography>
          {isShowInterviewCounter && (
            <Badge
              label={`${onsiteInterviewsCount} INTERVIEWS LEFT`}
              variant={hasNoInterviewSlots ? 'error' : 'warning'}
            />
          )}
        </Row>
      </Column>
      <Row alignItems="center" justifyContent="center" gap={4}>
        <img alt="user-icon" src={UserIcon} height={24} width={24} />
        <Text bold color={Colors.secondary_500} fontSize={22}>
          {totalCandidates}
        </Text>
      </Row>
      <Row alignItems="center" gap={8}>
        <Box
          backgroundColor={JobPostingStatusMapper.getColor(status)}
          borderRadius="100%"
          height={10}
          width={10}
        />
        <Text>{JobPostingStatusMapper.toHumanReadable(status)}</Text>
      </Row>
    </Row>
  );
};
